import React, { createContext, useContext, ReactElement, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { EAppRoutes } from '@common/enums';
import { useAuthContextProvider } from '@common/hooks/auth/AuthContextProvider';
import FullPageSpinnerLayout from '@common/layouts/FullPageSpinnerLayout';

const AuthenticatedRoutesContext = createContext<boolean | null>(null);

export const useIsAuthenticated = (): boolean | null => useContext(AuthenticatedRoutesContext);

const AuthenticatedRoutes: React.FC = (): ReactElement | null => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuthContextProvider();
  const [hasNavigated, setHasNavigated] = useState(false);

  // TODO: get rid of this effect, as it cause extra re-renders
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const state = query.get('state');
    const code = query.get('code');

    // Loading complete and user is not authenticated, redirect to login
    if (!isLoading && !isAuthenticated) {
      navigate(EAppRoutes.Login);
    }

    // Loading complete and user is authenticated and 'state' and 'code' query params are set, redirect to reporting and
    // remove those query params only. In this state we can assume the user has just logged in and is in local dev.
    if (!isLoading && isAuthenticated && state && code && !hasNavigated) {
      navigate(`${EAppRoutes.Reporting}`, { replace: true, state: { hideNavBar: false } });
      setHasNavigated(true);
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return <FullPageSpinnerLayout />;
  }

  return (
    <AuthenticatedRoutesContext.Provider value={isAuthenticated}>
      <Outlet />
    </AuthenticatedRoutesContext.Provider>
  );
};

export default AuthenticatedRoutes;
